import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Users, MapPin, Calendar, Coffee, MessageCircle, 
  Star, ArrowRight, Sparkles, Globe, Heart, Mail, CheckCircle
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import Leaderboard from "../components/Leaderboard";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Placeholder community features - these would be real features in production
// Events section removed - no real events exist yet

const testimonials = [
  {
    name: "Sarah M.",
    role: "UX Designer",
    city: "Reading",
    quote: "Absolutely brilliant! Found a cosy café near Old Street where I can crack on with work. I even met a few other freelancers.",
    avatar: "S"
  },
  {
    name: "James T.",
    role: "Software Developer",
    city: "Manchester",
    quote: "I needed to head to Oxford and had a couple of hours to kill. The work score feature is spot on. No more rubbish WiFi or noisy spaces.",
    avatar: "J"
  },
  {
    name: "Emma L.",
    role: "Content Writer",
    city: "Southampton",
    quote: "Remote working can be a bit lonely, but it's great to meet other users in little hidden gems!",
    avatar: "E"
  }
];

export default function CommunityPage() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [stats, setStats] = useState({ venues: 39, cities: 15, reviews: 39, remote_workers: 916 });

  // Fetch real stats from API
  useEffect(() => {
    axios.get(`${API}/public/stats`)
      .then(res => {
        if (res.data) {
          setStats({
            venues: res.data.venues || 39,
            cities: res.data.cities || 15,
            reviews: res.data.reviews || 39,
            remote_workers: res.data.remote_workers || 916
          });
        }
      })
      .catch(() => {/* Use defaults */});
  }, []);

  // Dynamic community stats based on API data
  const dynamicStats = [
    { label: "Remote Workers", value: `${(stats.remote_workers).toLocaleString()}+`, icon: Users },
    { label: "Cities Covered", value: `${stats.cities}+`, icon: Globe },
    { label: "Venues Listed", value: `${stats.venues}+`, icon: Coffee },
    { label: "Reviews Shared", value: `${(stats.reviews).toLocaleString()}+`, icon: Star }
  ];

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    
    setSubscribing(true);
    try {
      await axios.post(`${API}/mailing-list/subscribe`, {
        email: email.trim(),
        name: name.trim() || null
      });
      
      setSubscribed(true);
      setEmail("");
      setName("");
      toast.success("Successfully subscribed! We'll keep you posted.");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === "Email already subscribed") {
        toast.info("You're already subscribed!");
        setSubscribed(true);
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      <SEO 
        title="Join Our Remote Work Community"
        description="Connect with fellow remote workers across the UK. Find co-working buddies, join local meetups, share your favorite work spots, and build meaningful professional relationships."
        keywords="remote work community, digital nomad meetups UK, coworking events, freelancer networking, work from anywhere community, remote workers UK"
        canonical="/community"
      />
      <Header />
      
      <main className="pt-16">
        {/* Hero Section */}
        {/* Hero Section - Corporate Teal Theme */}
        <section className="relative bg-gradient-to-br from-brand via-brand-dark to-[#004d4d] text-white py-20 overflow-hidden">
          {/* Strider Logo Background */}
          <div 
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage: 'url(/strider-logo-bg.png)',
              backgroundSize: '400px',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'grayscale(100%) brightness(2)'
            }}
          />
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyem0wLTRWMjhIMjR2Mmgxem0tOC04djJoLTR2LTJoNHptMCAxMnYyaC00di0yaDR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
          
          <div className="max-w-6xl mx-auto px-4 relative">
            <motion.div 
              className="text-center max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm mb-6 backdrop-blur-sm">
                <Sparkles className="w-4 h-4" />
                <span>Join {(stats.remote_workers || 500).toLocaleString()}+ Remote Workers</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Work Alone, Together
              </h1>
              
              <p className="text-xl text-teal-100 mb-8">
                Connect with like-minded remote workers in your city. Share workspace recommendations, 
                join local meetups, and build your professional network.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/browse"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-brand rounded-xl font-semibold hover:bg-gray-100 transition-colors"
                  data-testid="find-workspace-btn"
                >
                  <Coffee className="w-5 h-5" />
                  Find a Workspace
                </Link>
                <a
                  href="#newsletter"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-xl font-semibold hover:bg-white/20 transition-colors border border-white/20"
                  data-testid="join-community-btn"
                >
                  <Users className="w-5 h-5" />
                  Join the Community
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {dynamicStats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-12 h-12 bg-brand/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <stat.icon className="w-6 h-6 text-brand" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Trustpilot Widget */}
        <section className="py-8 px-4 bg-white border-b border-gray-100">
          <div className="max-w-4xl mx-auto text-center">
            <div 
              className="trustpilot-widget mx-auto" 
              data-locale="en-GB" 
              data-template-id="56278e9abfbbba0bdcd568bc" 
              data-businessunit-id="69d42d3384dcd386e02c4686" 
              data-style-height="52px" 
              data-style-width="100%"
              data-token="698dee5f-e678-4ffe-bdcf-3aa76dfd0c00"
            >
              <a href="https://www.trustpilot.com/review/strider.work" target="_blank" rel="noopener noreferrer">
                See our reviews on Trustpilot
              </a>
            </div>
          </div>
        </section>

        {/* Leaderboard Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Contributor Leaderboard
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Our top contributors earn points by submitting venues and writing reviews. 
                Monthly gift cards and yearly trophies await!
              </p>
            </div>
            <Leaderboard />
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What Our Community Says
              </h2>
              <p className="text-gray-600">
                Real stories from remote workers across the UK
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.name}
                  className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`testimonial-star-${index}-${i}`} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand/10 rounded-full flex items-center justify-center text-brand font-semibold">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}, {testimonial.city}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Signup */}
        <section id="newsletter" className="py-16 px-4 bg-gradient-to-br from-brand via-brand-dark to-[#004d4d] text-white">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
              <MessageCircle className="w-8 h-8" />
            </div>
            
            <h2 className="text-3xl font-bold mb-4">
              Stay in the Loop
            </h2>
            <p className="text-teal-100 mb-8">
              Get weekly updates on new workspaces, community events, and exclusive tips for 
              working remotely across the UK.
            </p>

            {subscribed ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center justify-center gap-2 py-4 px-6 bg-white/10 rounded-xl backdrop-blur-sm"
              >
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="font-medium">Thanks for subscribing! We'll keep you posted.</span>
              </motion.div>
            ) : (
              <div className="max-w-md mx-auto space-y-4">
                {/* Google Auth Option */}
                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.setItem('auth_return_url', '/community#newsletter');
                    sessionStorage.setItem('newsletter_signup', 'true');
                    const redirectUrl = window.location.origin + '/contributor/auth/callback';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  className="w-full py-3 bg-white rounded-xl font-medium hover:bg-gray-50 transition-all flex items-center justify-center gap-3 text-gray-700"
                  data-testid="google-newsletter-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign up with Google
                </button>
                
                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/20"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-transparent text-white/70">or use email</span>
                  </div>
                </div>
                
                {/* Email Form */}
                <form onSubmit={handleSubscribe} className="space-y-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name (optional)"
                      className="flex-1 px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 placeholder-teal-200 text-white focus:outline-none focus:ring-2 focus:ring-white/30"
                      data-testid="newsletter-name"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="flex-1 px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 placeholder-teal-200 text-white focus:outline-none focus:ring-2 focus:ring-white/30"
                      data-testid="newsletter-email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={subscribing}
                    className="w-full sm:w-auto px-8 py-3 bg-white text-brand font-semibold rounded-xl hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 mx-auto disabled:opacity-70"
                    data-testid="newsletter-submit"
                  >
                    {subscribing ? "Subscribing..." : (
                      <>
                        <Mail className="w-4 h-4" />
                        Subscribe
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Find Your Workspace?
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Browse hundreds of laptop-friendly venues across the UK. Filter by WiFi speed, 
              noise level, power outlets, and more to find your ideal spot.
            </p>
            <Link
              to="/browse"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand text-white font-semibold rounded-xl hover:bg-brand-dark transition-colors"
              data-testid="browse-cta-btn"
            >
              Browse Workspaces
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
